import { useQuery } from '@apollo/client'
import {
  GetProductsQuery,
  GetProductsQueryVariables,
} from 'graphql/generated-types'
import { uniq } from 'lodash'
import { useMemo } from 'react'

import { GET_PRODUCTS } from '@/graphql'

export type ProductImageData = {
  preferredName: string | null
  name: string | null
  packagingName: string | null
  planogramImageUrl: string | null
  marketingImageUrl: string | null
  packaging: {
    __typename: 'ProductPackaging'
    quantity: number
    size: number | null
    unit: string | null
  } | null
}

const useProductsByUpc = (upcs: Array<string | null | undefined>) => {
  const normalizedUpcs = useMemo(
    () => uniq(upcs.filter(Boolean) as string[]).sort(),
    [upcs],
  )

  const { data, loading } = useQuery<
    GetProductsQuery,
    GetProductsQueryVariables
  >(GET_PRODUCTS, {
    variables: {
      filters: {
        upcs: normalizedUpcs,
      },
      pagination: {
        limit: normalizedUpcs.length,
        offset: 0,
      },
    },
    skip: normalizedUpcs.length === 0,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const productImageByUpc = useMemo(
    () =>
      Object.fromEntries(
        (data?.products.results || []).map(product => [
          product.upcA,
          {
            preferredName: product.preferredName || null,
            name: product.name || null,
            packagingName: product.packagingName || null,
            planogramImageUrl: product.planogramImageUrl || null,
            marketingImageUrl: product.marketingImageUrl || null,
            packaging: product.packaging ?? null,
          },
        ]),
      ) as Record<string, ProductImageData>,
    [data?.products.results],
  )

  return {
    upcs: normalizedUpcs,
    loading,
    productImageByUpc,
  }
}

export default useProductsByUpc
